// loop/argsErrorGate.js — E1-WS3 (harness-delivery-fix) truncated-args guard, EXTRACTED
// from runLocalAgentLoopInner so the E8 lifecycle-hook wiring fits under the spine's
// dp-loc ceiling. Behavior is byte-identical to the inline block it replaced: when a tool
// call's arguments arrived truncated/unparseable (__argsError), it is NOT executed —
// instead an honest error goes back to the model (and, once per turn, a nudge) so it
// resends complete JSON or writes the file in smaller pieces.
//
// Impure-by-injection: applies its effects through the loop's own onEvent /
// applySpineEvent ports (passed in), so the spine seam is a single `if (…) continue;`.

/**
 * @param {{ id: string, name: string, __argsError?: string }} tc
 * @param {object} s  loop run state
 * @param {{ onEvent: (e: any) => void, applySpineEvent: (s: any, e: any) => void }} ports
 * @returns {null | { control: 'continue' } | { control: 'return', value: object }}
 *   null                       — não era um args-error (spine segue o dispatch normal);
 *   {control:'continue'}       — tratado (denied + nudge), o spine deve `continue`;
 *   {control:'return', value}  — 2ª truncagem seguida da mesma tool: o spine ENCERRA o
 *                                turno com 'output_truncated' (não re-nudga em loop).
 * Mesma forma de retorno de applyToollessReply → o spine reusa o mesmo dispatch.
 */
export function handleArgsError(tc, s, { onEvent, applySpineEvent }) {
  if (!tc.__argsError) return null;

  const isWrite = tc.name === 'write_file' || tc.name === 'apply_edit';
  // WS-E (incidente tastedesign) — conta truncagens consecutivas da MESMA tool.
  applySpineEvent(s, { type: 'args_truncated', key: String(tc.name || '') });

  const base =
    `Erro: os argumentos da ferramenta ${tc.name} chegaram incompletos/truncados ` +
    `(${tc.__argsError}) — provável estouro do limite de tokens de saída no meio do ` +
    `JSON. NÃO foi executada.`;

  // ESCALA: 2ª truncagem seguida da mesma tool. Re-emitir o arquivo inteiro NÃO vai
  // caber no cap — encerra o turno em vez de queimar todas as iterações a zero entrega.
  if (s.truncationRetries >= 2) {
    // Mensagem específica por tipo de tool (review #6): write/edit não cabem inteiros no cap
    // → fatie; outras tools (run_command com heredoc grande, etc.) → mande args menores.
    const stopMsg = isWrite
      ? `${base} Esta é a ${s.truncationRetries}ª truncagem seguida de ${tc.name}: reenviar ` +
        `o conteúdo inteiro nunca vai caber no limite de saída. Encerrando o turno ` +
        `(output_truncated). Na próxima vez entregue em PARTES: write_file de um ESQUELETO ` +
        `(≤~150 linhas) e depois apply_edit REPETIDO para anexar o resto.`
      : `${base} Esta é a ${s.truncationRetries}ª truncagem seguida de ${tc.name}: os argumentos ` +
        `não cabem no limite de saída. Encerrando o turno (output_truncated). Na próxima vez ` +
        `envie argumentos MENORES/mais curtos (ex.: comando mais enxuto ou em etapas).`;
    onEvent({ type: 'tool_result', id: tc.id, name: tc.name, result: stopMsg, approved: false });
    applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: stopMsg } });
    onEvent({ type: 'loop_stop', stopReason: 'output_truncated', iterations: s.iteration + 1 });
    return { control: 'return', value: { ok: false, finalText: s.finalText, iterations: s.iteration + 1, stopReason: 'output_truncated' } };
  }

  // 1ª truncagem: nudge PRESCRITIVO e anti-re-emit (para write/edit, mande fatiar em
  // vez de reenviar o arquivo inteiro — que só truncaria de novo).
  const msg = isWrite
    ? `${base} NÃO reenvie o arquivo inteiro (vai truncar de novo). Em vez disso: ` +
      `write_file de um ESQUELETO (≤~150 linhas) e depois apply_edit REPETIDO para ` +
      `ANEXAR o resto em pedaços menores.`
    : `${base} Reenvie a tool_call COMPLETA com JSON válido, ou faça em partes menores.`;
  onEvent({ type: 'tool_result', id: tc.id, name: tc.name, result: msg, approved: false });
  applySpineEvent(s, { type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: msg } });
  if (!s.toolFailureNudged) {
    applySpineEvent(s, { type: 'nudge', which: 'toolFailure' });
    applySpineEvent(s, { type: 'convo_push', message: { role: 'user', content: msg } });
  }
  return { control: 'continue' };
}
